import BBOT_SERVER from "Config";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BbotAlert, BbotTabs, Breadcrumbs, Tabs, TOPContainer } from "top-component-library";
import { getDoveTailToolTipBodyText } from "../../pages/owner-app-dashboard/dovetail";
import CateringDraftOrdersTab from "./CateringDraftOrdersTab";
import PastOrdersLookupTab from "./PastOrdersLookupTab";

import axios from "axios";
import { generalErrorAlert, isFeatureEnabledById } from "util/Utils";
import { CATERING_FULFILLMENT_METHODS } from "Constants";

export const TAB_PAST_ORDERS = "past_order_lookup";
export const TAB_CATERING_DRAFTS = "catering_draft_orders";

function useCateringV2Latch(selectedCustomer, stations) {
  const [hasCateringV2, setHasCateringV2] = useState(false);
  useEffect(() => {
    setHasCateringV2(false);
  }, [selectedCustomer?.customer_id]);
  useEffect(() => {
    if (
      !hasCateringV2 &&
      isFeatureEnabledById("catering_v2", selectedCustomer) &&
      stations.some((s) => s.selected_fulfillment_methods?.some((m) => CATERING_FULFILLMENT_METHODS.includes(m)))
    ) {
      setHasCateringV2(true);
    }
  }, [stations, selectedCustomer, hasCateringV2]);
  return hasCateringV2;
}

const SearchPastOrdersPage = (props) => {
  const { selectedCustomer, userInfo, isSelectedCustomerFlaggedForDovetail } = props;

  const [stations, setStations] = useState([]);

  // Deep-link entry from the merchant-notification email passes ?tab=... so the
  // page opens on the Catering Draft Orders tab without a manual click. The
  // activeKey starts at the default and we flip it in an effect once the V2
  // latch confirms the catering tab will actually render; otherwise an AntD
  // activeKey-mismatch warning fires when a V1 owner lands on the deep link.
  const location = useLocation();
  const tabFromQuery = useMemo(() => new URLSearchParams(location.search).get("tab"), [location.search]);
  const [activeKey, setActiveKey] = useState(TAB_PAST_ORDERS);
  const userTouchedTabRef = useRef(false);

  const userIsAdmin = userInfo?.role === "admin";
  const doveTailToolTopBodyText = getDoveTailToolTipBodyText("Past Orders", isSelectedCustomerFlaggedForDovetail);

  const legacyPastOrdersPage = userIsAdmin
    ? BBOT_SERVER + "/superuser/console/#!/receipts"
    : BBOT_SERVER + "/owner/console/#!/receipts";

  const getStations = useCallback(async () => {
    try {
      const res = await axios.get("/owner/stations");
      setStations(res.data.stations || []);
    } catch (error) {
      generalErrorAlert(
        error,
        "Trouble getting stations details. Please refresh the page or contact DoorDash Support."
      );
    }
  }, []);

  useEffect(() => {
    if (!selectedCustomer) return;
    getStations();
  }, [selectedCustomer, getStations]);

  const hasCateringV2 = useCateringV2Latch(selectedCustomer, stations);

  // Apply the URL-driven tab pick only once the V2 latch confirms the
  // catering tab will render. If the user has already clicked a tab, leave
  // their pick alone; otherwise V1 customers stay on the default.
  useEffect(() => {
    if (userTouchedTabRef.current) return;
    if (tabFromQuery === TAB_CATERING_DRAFTS && hasCateringV2) {
      setActiveKey(TAB_CATERING_DRAFTS);
    }
  }, [tabFromQuery, hasCateringV2]);

  return (
    <TOPContainer>
      <Breadcrumbs name={"Past Order Lookup"} link={"/orders"} />
      <div className={"margin-bottom-3"}>
        <h2>
          Search Orders To <span className={"color-primary__dark"}>{selectedCustomer?.customer_name}</span>
        </h2>
      </div>

      {isSelectedCustomerFlaggedForDovetail && (
        <BbotAlert className="margin-bottom-1" message={doveTailToolTopBodyText} type="info" showIcon />
      )}

      <div className={"margin-bottom-3"}>
        <p>
          A start and end date filter is pre-applied. To narrow your search, please add filters using the Add Filter
          button. Updating the filters will automatically refresh the table.
          <br />
          The legacy Past Orders Page is available <a href={legacyPastOrdersPage}>here</a>.
        </p>
      </div>

      <BbotTabs
        id={"single-partner-tabs"}
        activeKey={activeKey}
        tabPosition={"top"}
        onChange={(key) => {
          userTouchedTabRef.current = true;
          setActiveKey(key);
          getStations(); //refresh stations in case changes to station refund reasons made
        }}
      >
        <Tabs.TabPane tab={"Past Order Lookup"} key={TAB_PAST_ORDERS}>
          <PastOrdersLookupTab
            userInfo={userInfo}
            selectedCustomer={selectedCustomer}
            stations={stations}
            isSelectedCustomerFlaggedForDovetail={isSelectedCustomerFlaggedForDovetail}
          />
        </Tabs.TabPane>
        {hasCateringV2 && (
          <Tabs.TabPane tab={"Catering Draft Orders"} key={TAB_CATERING_DRAFTS}>
            <CateringDraftOrdersTab selectedCustomer={selectedCustomer} />
          </Tabs.TabPane>
        )}
      </BbotTabs>
    </TOPContainer>
  );
};

export default SearchPastOrdersPage;
